import NiceModal from '@ebay/nice-modal-react';
import { useUserEventLogger } from '@repo/tracking-service';
import { BoxButton, Description, useToast } from '@repo/ui';
import { TermDrawer } from '@repo/user-sign';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import { useMount } from 'react-use';

import { COMMON_PATHS } from '@/lib/constants/paths';
import useHometaxBlockNotification from '@/lib/hooks/api/mutation/use-hometax-block-notification';
import { useHometaxBlockNotificationStatus, useHometaxBlockSettings } from '@/lib/hooks/api/query/use-hometax-block';
import { BusinessType } from '@/lib/types/request.ts';

export const QueueContent = ({ businessType }: { businessType: BusinessType }) => {
  const router = useRouter();
  const { toast } = useToast();
  const { sendClickEvent } = useUserEventLogger();
  const notificationStatus = useHometaxBlockNotificationStatus(businessType);
  const { settings, refresh } = useHometaxBlockSettings();
  const { commit } = useHometaxBlockNotification();
  const hasRequestedNotification = useRef(false);

  const waitingCount = businessType === 'corps' ? settings?.corpWaitingCount : settings?.waitingCount;
  const formattedWaitingCount = typeof waitingCount === 'number' && waitingCount > 0 ? waitingCount.toLocaleString('ko-KR') : '9,999';

  const handleClickConfirm = () => {
    sendClickEvent('lookup_waiting_confirm', {
      type: businessType,
      timing: settings?.queueScreenStage.toLowerCase()
    });
    router.replace(COMMON_PATHS.환급조회목록);
  };

  useEffect(() => {
    if (notificationStatus?.requested || hasRequestedNotification.current) return;
    if (businessType === 'indis' && !settings?.queueScreenEnabled) return;
    if (businessType === 'corps' && !settings?.corpQueueScreenEnabled) return;
    hasRequestedNotification.current = true;

    commit({
      variables: {
        businessType
      },
      onCompleted: (response, errors) => {
        hasRequestedNotification.current = false;
        if (errors?.length) {
          toast({ title: errors[0]?.message, variant: 'warning' });
          return;
        }

        const notificationResponse = response.requestHometaxBlockNotification;

        if (notificationResponse.errors?.message) {
          toast({ title: notificationResponse.errors.message, variant: 'warning' });
          return;
        }

        if (notificationResponse.result) {
          return;
        }

        toast({ title: '알 수 없는 문제가 발생했어요.', variant: 'warning' });
      },
      onError: () => {
        hasRequestedNotification.current = false;
        toast({ title: '알 수 없는 문제가 발생했어요.', variant: 'warning' });
      },
      updater: (store, response) => {
        if (!response?.requestHometaxBlockNotification?.result) return;

        const statusRecord = store.getRoot().getLinkedRecord('hometaxBlockNotificationStatus', { businessType });
        statusRecord?.setValue(true, 'requested');
      }
    });
  }, [businessType, commit, notificationStatus?.requested, settings?.corpQueueScreenEnabled, settings?.queueScreenEnabled, toast]);

  useMount(() => {
    refresh().catch(console.error);
  });

  return (
    <section className="container-medium pt-spacious-medium w-full">
      <Description
        title={
          <>
            홈택스 이용자가 많아
            <br />
            환급 조회에 시간이 걸려요
          </>
        }
      />
      <div className="w-full px-normal-xlarge relative">
        <img
          src={`${process.env.NEXT_PUBLIC_RESOURCE_CENTER_URL}/refund/image/img_receipt-bg.png`}
          className="w-full min-w-[272px] max-w-[400px] h-auto mx-auto"
          alt=""
        />
        <div className={'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col gap-tiny-large'}>
          <div className={'flex justify-center text-neutral-foreground-main display-small-bold'}>
            대기&nbsp;<span className={'text-primary-foreground-main'}>{formattedWaitingCount}</span>번
          </div>
          <div className={'text-center body-small-medium text-neutral-foreground-low'}>최대 24시간 소요될 수 있어요</div>
        </div>
      </div>
      <div className="pt-spacious-small container-padding-large flex flex-col items-center w-full gap-spacious-small">
        <div
          className={
            'p-normal-medium bg-neutral-background-lowest w-full rounded-bzc-medium caption-medium-semibold flex items-center gap-tiny-large text-neutral-foreground-main'
          }
        >
          <img src={`${process.env.NEXT_PUBLIC_RESOURCE_CENTER_URL}/common/iconography/icg_bell-dot_sm.svg`} className={'w-8 h-8'} alt="" />
          <div className={'break-all'}>
            조회 가능할 때 <span className={'text-primary-foreground-main'}>카카오톡/문자</span>로 안내드려요.
            <br />
            <a
              className={'text-neutral-foreground-low caption-medium-regular underline cursor-pointer'}
              onClick={() => {
                sendClickEvent('lookup_waiting_noti_guide', {
                  type: businessType,
                  timing: settings?.queueScreenStage.toLowerCase()
                });
                NiceModal.show(TermDrawer, { link: 'https://bznav.com/popup/terms/bznav-taxrefund-inform-notice', keepMounted: true });
              }}
            >
              수신 안내
            </a>
          </div>
        </div>
        <BoxButton variant="filled-primary" className={'w-fit'} onClick={handleClickConfirm}>
          확인
        </BoxButton>
      </div>
    </section>
  );
};
